// Navigation Configuration for hub.castready.org
// Centralized nav labels, routes, and CTA definitions

import { MAIN_SITE_LINKS } from "@/lib/domainConfig";
import { MODULES } from "@/lib/modules";

export interface NavItem {
  label: string;
  href: string;
  description?: string;
  badge?: string;
  external?: boolean;
}

export interface NavDropdown {
  label: string;
  items: NavItem[];
}

// Persona entry points - "For You" dropdown
export const personaNavItems: NavItem[] = [
  { label: "I'm a Parent", href: "/for/parents", description: "Youth actor training & safety" },
  { label: "I'm an Adult Actor", href: "/for/adult-actors", description: "Practice, castings & growth" },
  { label: "I'm a Coach/Studio", href: "/for/coaches", description: "Roster & student management" },
  { label: "I'm a Talent Rep", href: "/for/talent-reps", description: "Client readiness & submissions" },
  { label: "I'm in Casting/Production", href: "/for/casting-pros", description: "Casting workflow tools" },
  { label: "I'm a Screenwriter", href: "/for/screenwriters", description: "Script development & collaboration" },
  { label: "I'm an HMU Artist", href: "/for/hmu-artists", description: "Portfolio, bookings & growth" },
  { label: "I'm a Photographer/Creator", href: "/for/photographers", description: "Talent media & portfolio shoots" },
  { label: "I'm a Crew Member", href: "/for/crew-members", description: "Find gigs & build your reputation" },
  { label: "I'm a Producer", href: "/for/producers", description: "Staff your productions" },
  { label: "I'm an Industry Partner", href: "/partnerships", description: "Partnership opportunities" },
];

// Platform entry points — derived from the single MODULES registry (GTM order).
export const platformNavItems: NavItem[] = [
  ...[...MODULES]
    .sort((a, b) => a.order - b.order)
    .map((m): NavItem => ({
      label: m.name,
      href: m.marketingPath,
      description: m.description,
      ...(m.badge ? { badge: m.badge } : {}),
    })),
  { label: "Training Kits", href: "/training-kits", description: "Printed practice kits" },
  { label: "All modules →", href: "/platforms", description: "Browse the full catalogue" },
];

// Main header navigation. Marketing surfaces (personas, pricing
// pitch, founder pilot, blog) live on castready.org. Hub header
// surfaces only product-app entry points.
export const mainNavItems: (NavItem | NavDropdown)[] = [
  { label: "Product demos", href: "/demo" },
  { label: "Status", href: "/status" },
  { label: "Support", href: "/support" },
];

// CTA configurations
export interface CTAConfig {
  label: string;
  href: string;
  variant: "primary" | "secondary" | "ghost";
}

export const defaultCTAs = {
  primary: { label: "Get Started", href: "/login/callback-zone", variant: "primary" as const },
  secondary: { label: "Try Demo", href: "/demo", variant: "ghost" as const },
};

export const loggedInCTAs = {
  primary: { label: "Go to Dashboard", href: "/", variant: "primary" as const },
  secondary: { label: "Refer a Friend", href: "/referrals", variant: "ghost" as const },
};

// Referral nav item for authenticated users
export const referralNavItem: NavItem = {
  label: "Refer a Friend",
  href: "/referrals",
  description: "Earn credits by inviting friends",
};

// Role-based landing pages
export const roleLandingMap: Record<string, string> = {
  parent: "/parent-portal",
  family: "/parent-portal",
  coach: "/coach",
  agent: "/coach",
  manager: "/coach",
  casting_professional: "/castings",
  casting_director: "/castings",
  hmu_artist: "/hmu",
  crew_member: "/crew",
  producer: "/crew",
  photographer: "/capture",
  partner: "/pro",
  actor: "/callback-zone",
  admin: "/admin",
};

// Get role-based landing page
export const getRoleLanding = (roles: string[]): string => {
  // Priority order for multi-role users
  const priorityOrder = ["admin", "casting_professional", "casting_director", "coach", "agent", "manager", "hmu_artist", "crew_member", "producer", "parent", "family", "actor"];
  
  for (const role of priorityOrder) {
    if (roles.includes(role) && roleLandingMap[role]) {
      return roleLandingMap[role];
    }
  }
  
  return "/callback-zone";
};

// Footer configuration — wedge-first. Long-tail content lives on /legal-center, /trust-center, /platforms, /for.
export const footerConfig = {
  product: [
    { label: "Callback Zone", href: "/platforms/callback-zone" },
    { label: "CastReady Coach", href: "/platforms/coach" },
    { label: "CastReady Pro", href: "/platforms/pro" },
    { label: "Pricing", href: "/pricing" },
    { label: "All platforms", href: "/platforms" },
    { label: "Training Kits", href: "/training-kits" },
  ],
  company: [
    { label: "How It Works", href: "https://castready.org/how-it-works", external: true },
    { label: "Founder Pilot", href: "https://castready.org/founder-pilot", external: true },
    { label: "Who it's for", href: "https://castready.org/for", external: true },
    { label: "Blog", href: MAIN_SITE_LINKS.blog, external: true },
    { label: "About", href: MAIN_SITE_LINKS.about, external: true },
    { label: "Press", href: MAIN_SITE_LINKS.pressKit, external: true },
    { label: "Newsroom", href: MAIN_SITE_LINKS.newsroom, external: true },
    { label: "Careers", href: MAIN_SITE_LINKS.careers, external: true },
    { label: "Contact", href: MAIN_SITE_LINKS.contact, external: true },
  ],
  trust: [
    { label: "Privacy", href: "/privacy-policy" },
    { label: "Terms", href: "/terms-of-service" },
    { label: "Accessibility", href: "/accessibility" },
    { label: "Trust Center", href: "/trust-center" },
    { label: "Legal Center", href: "/legal-center" },
  ],
  // Source of truth for /legal-center page (kept here so the page stays in sync)
  legal: [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Service", href: "/terms-of-service" },
    { label: "Accessibility", href: "/accessibility" },
    { label: "AI Philosophy", href: "/ai-philosophy" },
    { label: "AI Fairness", href: "/ai-fairness" },
    { label: "AI Disclosure & Acceptable Use", href: "/legal/ai-disclosure" },
    { label: "Cookie Policy", href: "/legal/cookies" },
    { label: "DMCA / Copyright", href: "/legal/dmca" },
    { label: "Data Retention & Deletion", href: "/legal/data-retention" },
    { label: "Biometric & Voice Consent", href: "/legal/biometric-consent" },
    { label: "Refund Policy", href: "/legal/refunds" },
    { label: "Email & SMS Policy", href: "/legal/email-sms" },
    { label: "Marketplace Contractor Terms", href: "/legal/contractor-terms" },
    { label: "Community Guidelines", href: "/legal/community-guidelines" },
  ],
};

// Trust block content
export const trustBlockContent = `Your family's safety is our priority. CastReady is COPPA and FERPA compliant with moderated community spaces, parental controls, and privacy-first design. We never share data with third parties for advertising purposes. Young performers' content requires parental approval, and all AI feedback is designed to encourage growth—never criticize. Built by a parent and educator who wanted more for young actors.`;

// Type guard for dropdown
export const isNavDropdown = (item: NavItem | NavDropdown): item is NavDropdown => {
  return 'items' in item;
};
